// Pane-3 SENT BY / SENT TO / COPIED block for an artifact viewer.
// Resolves each address to a display name via
// artifact.client_context.colleagues[]; falls back to the raw email
// when no match exists. SENT BY shows the resolved name + source_date;
// SENT TO and COPIED are comma-separated lists. The COPIED row is
// omitted entirely when cc is empty. Block returns null when the
// artifact has no addressing info (e.g. documents).
//
// Shared between Workspace.tsx (right pane) and ArtifactViewer.tsx
// (full-page artifact viewer) — both surfaces render the same shape.

import type { ArtifactDetail } from '../api'
import { useAuth } from '../auth'
import { isPlayheadTenant } from '../playhead'

export function ArtifactRecipients({ artifact }: { artifact: ArtifactDetail }) {
  const rAuth = useAuth()
  const playhead = rAuth.status === 'authenticated' && isPlayheadTenant(rAuth.user.client?.code)
  const colleagues = artifact.client_context?.colleagues ?? []
  const byEmail = new Map<string, string>()
  for (const c of colleagues) {
    if (c?.email) byEmail.set(c.email.toLowerCase().trim(), c.name)
  }
  const resolve = (email: string): string => {
    const name = byEmail.get((email || '').toLowerCase().trim())
    return name || email
  }
  // Ken 2026-07-03: For documents referenced by URL in some email
  // (Drive-synced GDocs / uploaded files that came in as a link, not
  // a physical attachment), the doc's own from_email/to_emails/
  // cc_emails/source_date are empty because Drive-synced artifacts
  // don't carry a sender. Fall back to the referring email's
  // metadata via the server-side `linked_from` payload. The block
  // shows a "Referenced from email …" hint so it's clear where the
  // sender info is derived from.
  const linkedFrom = artifact.linked_from ?? null
  const isDerived = !artifact.from_email && !!linkedFrom
  const src = isDerived
    ? {
        from_email: linkedFrom!.from_email,
        to_emails: linkedFrom!.to_emails,
        cc_emails: linkedFrom!.cc_emails,
        source_date: linkedFrom!.source_date,
      }
    : {
        from_email: artifact.from_email,
        to_emails: artifact.to_emails,
        cc_emails: artifact.cc_emails,
        source_date: artifact.source_date,
      }
  const senderName = src.from_email ? resolve(src.from_email) : ''
  const timestamp = src.source_date
    ? new Date(src.source_date).toLocaleString()
    : ''
  const sentBy = [senderName, timestamp].filter(Boolean).join(' · ')
  const toList = (src.to_emails || []).map(resolve).join(', ')
  const ccList = (src.cc_emails || []).map(resolve).join(', ')
  if (!sentBy && !toList && !ccList) return null
  // Quiet pane-3 (Ken 2026-10-22, KEN/playhead): drop the "Sent By / Sent To /
  // Copied" field labels. Carry it typographically — sender NAME in primary with
  // the ADDRESS muted beside it, the DATE right-aligned; recipients muted below;
  // the cc line only when non-empty. No accent — it's the source document.
  if (playhead) {
    const senderEmail = src.from_email || ''
    const showEmail = senderEmail && senderName && senderName !== senderEmail
    return (
      <div className="mt-3">
        {isDerived && (
          <div className="mb-1 text-[11px] me-text-mute italic">
            Referenced from email{linkedFrom!.email_subject ? `: "${linkedFrom!.email_subject}"` : ''}
          </div>
        )}
        {(senderName || senderEmail) && (
          /* data-pane3-sender (UXT-10 10A): the first metadata line beneath the stem;
             the pane-2/pane-3 divider rule's top edge is measured from here. */
          <div data-pane3-sender className="flex items-baseline gap-2">
            <span className="me-connected me-recipient text-[13px] me-text truncate" style={{ color: 'var(--me-font-recipient-color, var(--me-text))' }}>{senderName || senderEmail}</span>
            {showEmail && <span className="me-recipient text-[11px] me-text-mute truncate">{senderEmail}</span>}
            {timestamp && <span className="me-recipient-date ml-auto shrink-0 text-[11px] me-text-mute tabular-nums">{timestamp}</span>}
          </div>
        )}
        {toList && (
          <div className="mt-0.5 break-words text-[11.5px] me-text-mute"><span className="me-recipient-label me-text-mute">to </span><span className="me-recipient">{toList}</span></div>
        )}
        {ccList && (
          <div className="mt-0.5 break-words text-[11.5px] me-text-mute"><span className="me-recipient-label me-text-mute">cc </span><span className="me-recipient">{ccList}</span></div>
        )}
      </div>
    )
  }
  return (
    <div className="mt-3 space-y-3">
      {isDerived && (
        <div className="text-[11px] me-text-mute italic">
          Referenced from email
          {linkedFrom!.email_subject ? `: "${linkedFrom!.email_subject}"` : ''}
        </div>
      )}
      {/* Labels in the identifier font (Noto Sans), title case (not all-caps). */}
      {sentBy && (
        <div>
          <div className="me-idfont mb-1 text-[10px] font-semibold me-text-mute">Sent By</div>
          <div className="me-connected text-[12.5px] me-text">{sentBy}</div>
        </div>
      )}
      {toList && (
        <div>
          <div className="me-idfont mb-1 text-[10px] font-semibold me-text-mute">Sent To</div>
          <div className="me-connected text-[12.5px] me-text break-words">{toList}</div>
        </div>
      )}
      {ccList && (
        <div>
          <div className="me-idfont mb-1 text-[10px] font-semibold me-text-mute">Copied</div>
          <div className="me-connected text-[12.5px] me-text break-words">{ccList}</div>
        </div>
      )}
    </div>
  )
}
